// Display-only masking of applicant contact data for internal users. The API still returns
// the real values, so mask at render time only — never write a masked value back into
// state that is submitted, searched or pushed to an ATS.

const MASK = '*'

/** "Yvonne" -> "Y****e", "Lin" -> "L*n", "Al" -> "A*". */
const maskWord = (word: string): string => {
  const chars = Array.from(word)
  if (chars.length <= 1) return MASK
  if (chars.length === 2) return chars[0] + MASK
  return chars[0] + MASK.repeat(chars.length - 2) + chars[chars.length - 1]
}

/** Masks each word of a name, keeping spaces and hyphens: "Anna-Lena Kraus" -> "A**a-L**a K***s". */
export const maskName = (value?: string | null): string => {
  if (!value) return ''
  return value
    .split(/([\s-]+)/)
    .map((part) => (/^[\s-]*$/.test(part) ? part : maskWord(part)))
    .join('')
}

/** "max.mustermann@gmail.com" -> "m************n@g***l.com". */
export const maskEmail = (value?: string | null): string => {
  if (!value) return ''
  const at = value.lastIndexOf('@')
  if (at < 0) return maskName(value)
  const local = value.slice(0, at)
  const domain = value.slice(at + 1)
  const dot = domain.lastIndexOf('.')
  const maskedDomain =
    dot > 0 ? `${maskWord(domain.slice(0, dot))}${domain.slice(dot)}` : maskWord(domain)
  return `${maskWord(local)}@${maskedDomain}`
}

/** Keeps the format and the last two digits: "+49 151 2345678" -> "+** *** *****78". */
export const maskPhone = (value?: string | null): string => {
  if (!value) return ''
  const totalDigits = (value.match(/\d/g) || []).length
  let seen = 0
  return value.replace(/\d/g, (digit) => {
    seen += 1
    return seen > totalDigits - 2 ? digit : MASK
  })
}

export interface ApplicantIdentity {
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  email?: string | null
  phone?: string | null
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Every caller passes its input through escapeRegExp, so the pattern is always a literal.
// eslint-disable-next-line security/detect-non-literal-regexp
const literalRegExp = (source: string, flags: string) => new RegExp(source, flags)

type Replacement = { pattern: RegExp; replace: (match: string) => string }

// Built per applicant: their email, phone and each name word, matched as whole words so a
// name like "Max" does not hit "Maximilian" or "max-width".
const buildReplacements = (applicant: ApplicantIdentity): Replacement[] => {
  const replacements: Replacement[] = []
  const { email, phone } = applicant
  if (email && email.trim()) {
    replacements.push({
      pattern: literalRegExp(escapeRegExp(email.trim()), 'gi'),
      replace: maskEmail,
    })
  }
  if (phone && phone.trim() && (phone.match(/\d/g) || []).length >= 4) {
    replacements.push({
      pattern: literalRegExp(escapeRegExp(phone.trim()), 'g'),
      replace: maskPhone,
    })
  }
  const nameWords = new Set(
    [applicant.firstName, applicant.lastName, applicant.fullName]
      .flatMap((name) => (name || '').split(/[\s-]+/))
      .filter((word) => Array.from(word).length >= 2)
  )
  // Longest first, so "Anna-Lena" style overlaps are masked by the most specific word.
  Array.from(nameWords)
    .sort((a, b) => b.length - a.length)
    .forEach((word) => {
      replacements.push({
        pattern: literalRegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(word)}(?![\\p{L}\\p{N}])`, 'giu'),
        replace: maskWord,
      })
    })
  return replacements
}

const applyReplacements = (text: string, replacements: Replacement[]): string =>
  replacements.reduce((result, { pattern, replace }) => result.replace(pattern, replace), text)

/** Masks the applicant's own name, email and phone wherever they occur in free text. */
export const maskApplicantInText = (
  text: string | null | undefined,
  applicant: ApplicantIdentity
): string => {
  if (!text) return text || ''
  return applyReplacements(text, buildReplacements(applicant))
}

/**
 * Same as maskApplicantInText, but for an HTML document (email bodies): only text nodes and
 * a few human-readable attributes are touched, so markup, CSS and links keep working.
 */
export const maskApplicantInHtml = (
  html: string | null | undefined,
  applicant: ApplicantIdentity
): string => {
  if (!html) return html || ''
  const replacements = buildReplacements(applicant)
  if (!replacements.length || typeof DOMParser === 'undefined') return html
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const walker = doc.createTreeWalker(doc.documentElement, NodeFilter.SHOW_TEXT)
    let node = walker.nextNode()
    while (node) {
      const parentTag = node.parentElement?.tagName
      if (parentTag !== 'STYLE' && parentTag !== 'SCRIPT' && node.nodeValue) {
        node.nodeValue = applyReplacements(node.nodeValue, replacements)
      }
      node = walker.nextNode()
    }
    const readableAttributes = ['title', 'alt']
    doc.querySelectorAll('[title], [alt]').forEach((el) => {
      readableAttributes.forEach((attr) => {
        const value = el.getAttribute(attr)
        if (value) el.setAttribute(attr, applyReplacements(value, replacements))
      })
    })
    // mailto: links would still carry the address.
    doc.querySelectorAll('a[href^="mailto:"]').forEach((el) => {
      el.setAttribute('href', applyReplacements(el.getAttribute('href') || '', replacements))
    })
    const doctype = /^\s*<!doctype[^>]*>/i.exec(html)?.[0] || ''
    return doctype + doc.documentElement.outerHTML
  } catch {
    return html
  }
}
